'use client';

import Icon from '@/components/ui/AppIcon';

interface PaymentOptionsProps {
  paymentMethod: string;
  onPaymentMethodChange: (method: string) => void;
  currency: 'INR' | 'USD';
}

export default function PaymentOptions({
  paymentMethod,
  onPaymentMethodChange,
  currency,
}: PaymentOptionsProps) {
  const paymentMethods = [
    {
      id: 'card',
      name: 'Credit/Debit Card',
      icon: 'CreditCardIcon',
      description: 'Visa, Mastercard, American Express',
    },
    {
      id: 'upi',
      name: 'UPI Payment',
      icon: 'DevicePhoneMobileIcon',
      description: 'Google Pay, PhonePe, Paytm',
    },
    {
      id: 'netbanking',
      name: 'Net Banking',
      icon: 'BuildingLibraryIcon',
      description: 'All major Indian banks',
    },
    {
      id: 'corporate',
      name: 'Corporate Account',
      icon: 'BuildingOfficeIcon',
      description: 'Bill to company account',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-semibold text-[var(--color-text-primary)] mb-2">
          Payment Method
        </h2>
        <p className="text-[var(--color-text-secondary)]">
          Choose your preferred payment method
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {paymentMethods.map((method) => (
          <div
            key={method.id}
            onClick={() => onPaymentMethodChange(method.id)}
            className={`relative p-6 rounded-xl cursor-pointer transition-all duration-300 ${
              paymentMethod === method.id
                ? 'ring-2 ring-[var(--color-accent)] bg-[var(--color-accent)]/5'
                : 'ring-1 ring-[var(--color-border)] hover:ring-[var(--color-primary)] bg-white'
            }`}
          >
            <div className="flex items-start gap-4">
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  paymentMethod === method.id
                    ? 'bg-[var(--color-accent)] text-white'
                    : 'bg-[var(--color-muted)] text-[var(--color-text-secondary)]'
                }`}
              >
                <Icon name={method.icon as any} size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-1">
                  {method.name}
                </h3>
                <p className="text-sm text-[var(--color-text-secondary)]">{method.description}</p>
              </div>
              {paymentMethod === method.id && (
                <Icon name="CheckCircleIcon" size={24} className="text-[var(--color-accent)]" />
              )}
            </div>
          </div>
        ))}
      </div>

      {paymentMethod === 'card' && (
        <div className="bg-white rounded-xl p-6 ring-1 ring-[var(--color-border)] space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--color-text-primary)]">
              Card Number
            </label>
            <input
              type="text"
              placeholder="1234 5678 9012 3456"
              className="w-full px-4 py-3 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--color-text-primary)]">
                Expiry Date
              </label>
              <input
                type="text"
                placeholder="MM/YY"
                className="w-full px-4 py-3 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--color-text-primary)]">
                CVV
              </label>
              <input
                type="text"
                placeholder="123"
                className="w-full px-4 py-3 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300"
              />
            </div>
          </div>
        </div>
      )}

      {paymentMethod === 'corporate' && (
        <div className="bg-[var(--color-muted)] rounded-lg p-4 flex items-start gap-3">
          <Icon name="InformationCircleIcon" size={24} className="text-[var(--color-primary)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[var(--color-text-primary)] mb-1">
              Corporate Account Required
            </p>
            <p className="text-xs text-[var(--color-text-secondary)]">
              You'll need to provide your corporate account details. Our team will verify and process your booking within 24 hours.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-6 ring-1 ring-[var(--color-border)]">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="ShieldCheckIcon" size={24} className="text-[var(--color-success)]" />
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
            Secure Payment
          </h3>
        </div>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Your payment information is encrypted and secure. We use industry-standard SSL encryption to protect your data.
        </p>
      </div>
    </div>
  );
}